import React from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { UserObj } from "../model/user";

import Amplify from "aws-amplify";
import { Auth, Hub } from "aws-amplify";
import { COGNITO } from "../configs/aws";

import ApiFetch from "../services/api.fetch";
import { useNavigate } from "react-router-dom";

interface user_auth_context {
  user: any | null | undefined;
  loading: boolean | null;
  setLoading: any;
  updateUser: (updatedUserInfo: any) => Promise<void>;
  confirmSignUp: (username: any, code: any) => Promise<void>;
  createTenant: (
    firstName: string,
    lastName: string,
    phoneNumber: string,
    email: string,
    password: string
  ) => any;
  signIn: (username: string, password: string) => Promise<void>;
  signUp: (username: string, email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  reloadUserData: any;
  forgotPassword: (newPassword: string) => Promise<void>;
  passwordExpired: (
    username: any,
    oldPassword: string,
    newPassword: string,
    userAttributes: any
  ) => Promise<void>;
  resendConfirmationCode: (username: string) => Promise<void>;
}

const UserAuthContext = createContext<user_auth_context>({
  user: null,
  loading: null,
  setLoading: null,
  resendConfirmationCode: async (username: string) => {
    console.log("STUB: resendSignUp");
  },
  confirmSignUp: async (username: any, code: any) => {
    console.log("STUB: Confirm SignUp");
  },
  updateUser: async (updatedUserInfo: any) => {
    console.log("STUB: Update");
  },
  createTenant: async (
    firstName: string,
    lastName: string,
    phoneNumber: string,
    email: string,
    password: string
  ) => {
    console.log("STUB: tenant");
  },
  signIn: async (username: string, password: string) => {
    console.log("STUB: SignIn");
  },
  signOut: async () => {
    console.log("STUB: SignOut");
  },
  signUp: async (username: string, email: string, password: string) => {
    console.log("STUB: SignUp");
  },
  forgotPassword: async (username: string) => {
    console.log("STUB: Update Password");
  },
  reloadUserData: async () => {
    console.log("STUB: Reload User Data");
  },
  passwordExpired: async (
    username: any,
    oldPassword: string,
    newPassword: string,
    userAttributes: any
  ) => {
    console.log("STUB: Update Password");
  },
});

Amplify.configure({
  aws_cognito_region: COGNITO.REGION,
  aws_user_pools_id: COGNITO.USER_POOL_ID,
  aws_user_pools_web_client_id: COGNITO.APP_CLIENT_ID,
});

export const UserAuthContextProvider = ({
  children,
}: React.PropsWithChildren) => {
  const navigate = useNavigate();
  const [user, setUser] = useState<UserObj | null | undefined>(null);
  const [loading, setLoading] = useState(true);

  const signIn = async (username: string, password: string) => {
    setLoading(true);
    try {
      const response = await Auth.signIn(username, password);
      setLoading(false);

      if (response.challengeName === "NEW_PASSWORD_REQUIRED") {
        navigate("/passwordexpired/");
      }

      return response;
    } catch (error: any) {
      return error;
    }
  };

  const resendConfirmationCode = async (username: string) => {
    Auth.resendSignUp(username);
  };

  const passwordExpired = async (
    username: any,
    oldPassword: string,
    newPassword: string,
    userAttributes = {}
  ) => {
    const user = await Auth.signIn(username, oldPassword);
    try {
      // Complete the new password challenge
      const updatedUser = await Auth.completeNewPassword(
        user, // the Cognito User Object
        newPassword, // the new password
        userAttributes // optional, additional user attributes
      );
      return updatedUser;
    } catch (error: any) {
      // Handle any errors that occur
      throw new Error(error.message);
    }
  };

  const confirmSignUp = async (username: any, code: string) => {
    try {
      const response = await Auth.confirmSignUp(username, code);
      console.log(response);
    } catch (error: any) {
      console.error("ERROR: ", error.message);
      throw new Error(error.message);
    }
  };

  const changePassword = async (oldPassword: string, newPassword: string) => {
    try {
      // Get the current authenticated user
      const user = await Auth.currentAuthenticatedUser();
      // Change the password for the user
      const response = await Auth.changePassword(
        user,
        oldPassword,
        newPassword
      );
      return response; // The response is typically "SUCCESS" on successful password change
    } catch (error: any) {
      // Handle any errors that occur
      throw new Error(error.message);
    }
  };

  const signUp = async (username: string, email: string, password: string) => {
    const singUpParams = {
      attributes: {
        email: email,
      },
      username: username,
      password: password,
    };

    await Auth.signUp(singUpParams).then((data: any) => {
      console.log(data);
    });
  };

  const createTenant = async (
    firstName: string,
    lastName: string,
    phoneNumber: string,
    email: string,
    password: string
  ) => {
    await signOut();
    let userExists = false;
    if (userExists) {
      throw Error("User already exists");
    }

    if (!userExists) {
      try {
        const payload = {
          name: firstName,
          surname: lastName,
          email: email,
          contactNumber: phoneNumber,
          isPremium: true,
        };

        const data = await ApiFetch.httpPostPublic(
          "v1/public/tenant/register",
          payload
        )
        await signUp(data.cognitoUsername, email, password);

        return data;
      } catch (error: any) {
        throw new Error(error);
      }
    }
  };

  const updateUser = async (updatedUserInfo: any) => {
    return ApiFetch.httpPut("v1/user/update", updatedUserInfo).then(() => {
      ApiFetch.httpGet(
        `v1/user/cognito/${updatedUserInfo.cognitoUsername}`
      ).then((userInfo) => {
        _setUserSessionInfo(userInfo);
      });
    });
  };

  const signOut = async () => {
    try {
      const response = await Auth.signOut();
      setUser(null);
      sessionStorage.removeItem("user_session");
      return response;
    } catch (error: any) {
      throw new Error(error.message);
    }
  };

  const forgotPassword = async (username: string) => {
    try {
      const result = await Auth.forgotPassword(username);
    } catch (error: any) {
      console.log("ERROR: ", error.message);
    }
  };

  const reloadUserData = async () => {
    setLoading(true);
    _getUserSessionInfo();
    let userInfo = null;
    try {
      const response = await Auth.currentAuthenticatedUser();
      try {
        userInfo = await ApiFetch.httpGet(
          `v1/user/email/${response.attributes.email}`
        );
      } catch (e: any) {
        setLoading(false);
        return "error";
      }
      try {
        const tenantInfo = await ApiFetch.httpGet(
          `v1/tenant/byUserId/${userInfo.id}`
        );
        userInfo.tenant = tenantInfo;
      } catch (e: any) {
        setLoading(false);
        return "error";
      }
      _setUserSessionInfo(userInfo);
      const { attributes, signInUserSession } = response;
      setLoading(false);
      return { attributes, jwtToken: signInUserSession.accessToken.jwtToken };
    } catch (error: any) {
      setLoading(false);
      return "error";
    } finally {
      setLoading(false);
    }
  };

  const _getUserSessionInfo = () => {
    const userSessionData = sessionStorage.getItem("user_session");
    if (userSessionData) {
      const userSession = JSON.parse(userSessionData);
      setUser(userSession);
      setLoading(false);
    } else {
      setLoading(false);
    }
  };

  const _setUserSessionInfo = (userInfo: any) => {
    sessionStorage.setItem("user_session", JSON.stringify(userInfo));
    setUser(userInfo);
  };

  useEffect(() => {
    _getUserSessionInfo();
    Hub.listen("auth", (u) => {
      reloadUserData();
    });
    reloadUserData();
  }, []);

  return (
    <UserAuthContext.Provider
      value={{
        user,
        loading,
        setLoading,
        updateUser,
        createTenant,
        reloadUserData,
        signIn,
        signUp,
        signOut,
        forgotPassword,
        confirmSignUp,
        passwordExpired,
        resendConfirmationCode
      }}
    >
      {children}
    </UserAuthContext.Provider>
  );
};

export const useUserAuth = () => {
  return useContext<user_auth_context>(UserAuthContext);
};
